window.StockCubeCore = window.StockCubeCore || {};
window.StockCubeCore.updateBarsSectors = function (stocks, topN = Infinity, colorMode = 'gradient', isFixed = false) {
    this._billboardEls = null;
    if (this.data && ['sip-sec', 'dividend-sec'].includes(this.data.dataSource)) {
        isFixed = false;
    }

    const container = this.el;
    const heightValueFn = this.heightValueFn;
    const labelValueFn = this.labelValueFn;
    const usableHeight = this.usableHeight;
    const targetHeight = usableHeight * 0.9;

    const minColumns = 1;
    const maxColumns = 11;
    const sectorIds = this.data.sectorIds.map(Number).sort((a, b) => a - b);
    const numColumns = Math.max(minColumns, Math.min(sectorIds.length, maxColumns));
    this.numColumns = numColumns;

    const spacing = this.spacing !== undefined ? this.spacing : 0.05;
    const { barWidth, barDepth } = this.resolveBarGeometry(this.el, this.containerHeight, numColumns, spacing);
    this.barDepth = barDepth;

    const filteredStocksForUpdate = this.applySipFiltersToStocks(stocks);

    const stockMap = new Map(filteredStocksForUpdate.map(stock => [stock.ticker, stock]));

    let allHeightValues;
    if (this.data && ['dividend-sec', 'sip-sec'].includes(this.data.dataSource)) {
        allHeightValues = filteredStocksForUpdate.map(stock => heightValueFn(stock));
    } else {
        allHeightValues = Array.from(this.stockBars.values()).map(bar => {
            const stock = stockMap.get(bar.lastLabel.split('\n')[0]);
            return stock ? heightValueFn(stock) : bar.heightValue;
        });
    }

    const currentFrameMax = Math.max(...allHeightValues.map(val => Math.abs(val)));
    let maxHeightValue = window.StockCubeCore.trackMaxHeightValue.call(
        this, currentFrameMax, heightValueFn
    );
    const hasNegativeValues = window.StockCubeCore.resolveNegativeValues.call(this, allHeightValues);

    this.zeroPlane = 0;

    const heightScale = window.StockCubeCore.computeHeightScale(targetHeight, maxHeightValue, hasNegativeValues);
    this.heightScale = heightScale;

    const { sectorIdToColIndex, sectorColors } = window.StockCubeCore.buildSectorMapping(sectorIds, numColumns);

    const maxRows = Math.min(topN, Math.max(...sectorIds.map(sectorId =>
        Array.from(this.stockBars.entries())
            .filter(([_, bar]) => bar.sectorId === sectorId)
            .length
    )));

    const sectorAverages = window.StockCubeCore.computeSectorAverages(this.stockBars);

    let globalMinHeight = 0;
    let hasNegativeBars = false;
    sectorIds.forEach((sectorId) => {
        const sectorBars = Array.from(this.stockBars.entries())
            .filter(([_, bar]) => bar.sectorId === sectorId);

        if (sectorBars.length > 0) {
            const sectorHeights = sectorBars.map(([ticker, bar]) => {
                const stock = stockMap.get(ticker);
                const heightValue = stock ? heightValueFn(stock) : bar.heightValue;
                return this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
            });
            const sectorMinHeight = Math.min(...sectorHeights);

            if (sectorMinHeight < 0) {
                hasNegativeBars = true;
                globalMinHeight = Math.min(globalMinHeight, sectorMinHeight);
            }
        }
    });

    const labelY = hasNegativeBars
        ? this.zeroPlane + globalMinHeight - 0.05
        : this.zeroPlane - 0.05;

    sectorIds.forEach((sectorId) => {
        const avg = sectorAverages.get(sectorId) || { sum: 0, count: 0 };
        const avgHeightValue = avg.count > 0 ? avg.sum / avg.count : 0;
        const sectorLabel = this.sectorLabels.get(sectorId);
        if (sectorLabel) {
            sectorLabel.setAttribute("value", this.sectorNames[sectorId] || `Sector ${sectorId}\n${avgHeightValue.toFixed(2)}%`);
            const labelColor = colorMode === 'gradient' ? this.getGradientColor(avgHeightValue) : sectorColors.get(sectorId) || '#FFFFFF';
            sectorLabel.setAttribute("color", labelColor);
            const colIndex = sectorIdToColIndex.get(sectorId);
            const x = colIndex * (barWidth + spacing) - ((numColumns - 1) * (barWidth + spacing)) / 2;

            sectorLabel.intendedX = x;
            const sectorLabelStyle = this.platformConfig ? this.platformConfig.sectorLabelStyle : {};
            const zOffset = sectorLabelStyle.zOffset || 0.2;
            const yOffset = sectorLabelStyle.yOffset || 0;
            const xOffset = (sectorLabelStyle.xOffsets || {})[sectorId] || 0;
            sectorLabel.intendedXOffset = xOffset;
            sectorLabel.setAttribute("position", `${x + xOffset} ${labelY + yOffset} ${zOffset}`);
            sectorLabel.baseLabelY = labelY;

            const hasAnyStocks = filteredStocksForUpdate.some(s => s.sector_id === sectorId);
            sectorLabel.setAttribute('visible', hasAnyStocks);
        }
    });

    let positionMap = new Map();

    const hasInitialPositions = Array.from(this.stockBars.values()).every(bar =>
        typeof bar.x !== 'undefined' && typeof bar.z !== 'undefined'
    );

    if (isFixed && hasInitialPositions) {
        positionMap = window.StockCubeCore.buildFixedSectorPositionMap(this.stockBars);
    } else {
        const allowedTickers = new Set();
        sectorIds.forEach((sectorId) => {
            const sortFn = this.sortOrderFn || heightValueFn;
            const direction = this.sortDirection || 'desc';

            const sectorStocks = filteredStocksForUpdate
                .filter(s => s.sector_id === sectorId)
                .map(s => ({
                    ticker: s.ticker,
                    height: this.calculateBarHeight(heightValueFn(s), heightScale, maxHeightValue, targetHeight),
                    stock: s
                }))
                .sort((a, b) => {
                    const valA = sortFn(a.stock);
                    const valB = sortFn(b.stock);
                    return direction === 'asc' ? valA - valB : valB - valA;
                })
                .slice(0, topN);

            const x = sectorIdToColIndex.get(sectorId) * (barWidth + spacing) - ((numColumns - 1) * (barWidth + spacing)) / 2;
            sectorStocks.forEach((stock, rank) => {
                const { z } = this.calculateZPositions(maxRows, sectorStocks.length, 'sector', 0, rank);
                positionMap.set(stock.ticker, { x, z });
                allowedTickers.add(stock.ticker);
            });
        });

        window.StockCubeCore.removeOrphanedBars(this.stockBars, positionMap, this.el);

        sectorIds.forEach((sectorId) => {
            const x = sectorIdToColIndex.get(sectorId) * (barWidth + spacing) - ((numColumns - 1) * (barWidth + spacing)) / 2;
            const sortFn = this.sortOrderFn || heightValueFn;
            const direction = this.sortDirection || 'desc';

            const ordered = filteredStocksForUpdate
                .filter(s => s.sector_id === sectorId)
                .map(s => ({
                    stock: s,
                    height: this.calculateBarHeight(heightValueFn(s), heightScale, maxHeightValue, targetHeight)
                }))
                .sort((a, b) => {
                    const valA = sortFn(a.stock);
                    const valB = sortFn(b.stock);
                    return direction === 'asc' ? valA - valB : valB - valA;
                })
                .slice(0, topN);

            ordered.forEach((entry, rank) => {
                const ticker = entry.stock.ticker;
                if (this.stockBars.has(ticker)) return;
                const { z } = this.calculateZPositions(maxRows, ordered.length, 'sector', 0, rank);
                const heightValue = heightValueFn(entry.stock);
                const height = this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
                const absHeight = Math.max(0.001, Math.abs(height));
                const isPositive = height >= 0;
                const barBaseY = isPositive ? this.zeroPlane + (absHeight / 2) : this.zeroPlane - (absHeight / 2);
                const color = colorMode === 'gradient' ? this.getGradientColor(heightValue) : (sectorColors.get(sectorId) || '#FFFFFF');
                const created = this.createStockBar(
                    entry.stock, x, z, height, color, labelValueFn, container,
                    this.zeroPlane, barWidth, barDepth, 'true'
                );
                this.stockBars.set(ticker, {
                    bar: created.bar,
                    primaryLabel: created.primaryLabel,
                    x,
                    z,
                    sectorId,
                    heightValue,
                    y: barBaseY,
                    lastHeight: height,
                    lastLabel: labelValueFn(entry.stock)
                });
            });
        });
    }

    const isMmapChart = this.data?.dataSource === 'mmap';
    const isTopDollarVol = this.data.dataSource === 'top-dollar-vol' || this.data.dataSource === 'top-dollar-vol-sec';

    for (const [ticker, barInfo] of this.stockBars) {
        const stock = stockMap.get(ticker);
        const stockDetails = { ...stock };
        let heightValue, height, labelText, color;

        if (stock) {
            heightValue = heightValueFn(stock);
            height = this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
            labelText = labelValueFn(stock);
            if (colorMode === 'gradient') {
                color = this.getGradientColor(heightValue);
            } else if (colorMode === 'sector') {
                color = sectorColors.get(barInfo.sectorId) || '#FFFFFF';
            } else {
                throw new Error(`Invalid colorMode: ${colorMode}. Must be 'gradient' or 'sector'.`);
            }
            if (colorMode === 'gradient') {
                window.StockCubeCore.applyGradientColorAnimations(barInfo.bar, color);
                if (barInfo.bar.__coloredBarNoTexture) {
                    window.StockCubeCore.applyGradientColorAnimations(barInfo.bar.__coloredBarNoTexture, color);
                }
            }

            if (typeof setAttrIfChanged === 'function') {
                if (setAttrIfChanged(barInfo.bar, 'color', color)) {
                    barInfo.bar.__currentColor = color;
                }
                if (barInfo.bar.__coloredBarNoTexture) {
                    setAttrIfChanged(barInfo.bar.__coloredBarNoTexture, 'color', color);
                    barInfo.bar.__coloredBarNoTexture.__currentColor = color;
                }
            } else {
                barInfo.bar.setAttribute('color', color);
            }

            const barMesh = barInfo.bar.getObject3D('mesh');
            if (!this.isFirstRender && barMesh && barMesh.material && !isTopDollarVol) {
                barMesh.material.color.set(color);
            } else {
                const materialConfig = {
                    color: color,
                    opacity: isMmapChart ? 1.0 : 0.65,
                    transparent: isTopDollarVol ? true : (isMmapChart ? false : true),
                    side: 'double',
                    shader: isMmapChart ? 'flat' : undefined,
                    blending: 'normal',
                    depthTest: true,
                    depthWrite: true
                };
                if (isTopDollarVol) {
                    barInfo.bar.setAttribute('material', materialConfig);
                } else if (typeof setAttrIfChanged === 'function') {
                    setAttrIfChanged(barInfo.bar, 'material', materialConfig);
                } else {
                    barInfo.bar.setAttribute('material', materialConfig);
                }
            }
            barInfo.bar.__stockData = stockDetails;
            barInfo.bar.setAttribute('stock-bar', 'baseColor', color);
            barInfo.stock = stockDetails;
            this.applyEmissiveHighlight(barInfo.bar, stockDetails, color);
        } else {
            heightValue = barInfo.heightValue;
            height = this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
            labelText = barInfo.lastLabel;
            color = barInfo.bar.getAttribute('color');
        }

        const { absHeight, barBaseY, primaryLabelY } = window.StockCubeCore.computeBarYPositions(height, this.zeroPlane, this.labelYOffset, targetHeight);

        const pos = positionMap.get(ticker);
        if (!pos) {
            continue;
        }

        barInfo.bar.removeAttribute('animation');
        barInfo.bar.removeAttribute('animation__position');
        barInfo.primaryLabel.removeAttribute('animation');

        barInfo.bar.setAttribute('animation', {
            property: 'height',
            to: absHeight,
            dur: 400,
            easing: 'easeInOutQuad'
        });

        const positionAnimName = `animation__pos_${ticker}`;
        barInfo.bar.setAttribute(positionAnimName, {
            property: 'position',
            to: `${pos.x} ${barBaseY} ${-pos.z}`,
            dur: 400,
            easing: 'easeInOutQuad'
        });

        barInfo.primaryLabel.setAttribute('value', labelText);

        let finalLabelY = primaryLabelY;
        if (this.rotationData) {
            barInfo.lastHeight = height;
            const rotationBasedPosition = this.calculateLabelPosition?.(barInfo, this.rotationData, pos.x, -pos.z);
            if (rotationBasedPosition) {
                finalLabelY = rotationBasedPosition.y;
            }
        }

        barInfo.primaryLabel.setAttribute('animation', {
            property: 'position',
            to: `${pos.x} ${finalLabelY} ${-pos.z}`,
            dur: 400,
            easing: 'easeInOutQuad'
        });

        if (isMmapChart && barInfo.companyIcon) {
            this.updateCompanyIconPosition?.(barInfo);
        }

        barInfo.x = pos.x;
        barInfo.z = pos.z;
        barInfo.y = barBaseY;
        barInfo.heightValue = heightValue;
        barInfo.lastHeight = height;
        barInfo.lastLabel = labelText;
        this.stockBars.set(ticker, barInfo);
    }

    setTimeout(() => {
        this.updateBillboards?.(true);
    }, 100);

    const isAnimationOn = typeof this.getConfigSetting === 'function' ? !!this.getConfigSetting('isAnimationOn', true) : true;
    const intervals = this.currentData && this.currentData.intervals;
    const numFrames = intervals ? Object.keys(intervals).length : 0;

    if (numFrames === 1 || !isAnimationOn) {
        const finalSectorAverages = window.StockCubeCore.computeSectorAverages(this.stockBars);
        sectorIds.forEach((sectorId) => {
            const avg = finalSectorAverages.get(sectorId) || { sum: 0, count: 0 };
            const avgHeightValue = avg.count > 0 ? avg.sum / avg.count : 0;
            const sectorLabel = this.sectorLabels.get(sectorId);
            if (sectorLabel) {
                sectorLabel.setAttribute("value", this.sectorNames[sectorId] || `Sector ${sectorId}\n${avgHeightValue.toFixed(2)}%`);
                const labelColor = colorMode === 'gradient' ? this.getGradientColor(avgHeightValue) : sectorColors.get(sectorId) || '#FFFFFF';
                sectorLabel.setAttribute("color", labelColor);
            }
        });
    }

    this.calculateSectorLabelPosition?.(this.rotationData);
};

window.StockCubeCore.updateBarsNoSectors = function (stocks, topN = Infinity, fixed = false) {
    this._billboardEls = null;
    const container = this.el;
    const heightValueFn = this.heightValueFn;
    const labelValueFn = this.labelValueFn;
    const usableHeight = this.usableHeight;
    const targetHeight = usableHeight * 0.9;

    const spacing = this.spacing !== undefined ? this.spacing : 0.03;
    const { barWidth, barDepth } = this.resolveBarGeometry(this.el, this.containerHeight, 11, spacing);
    const geometry = this.el.getAttribute('geometry') || { width: this.containerHeight };
    const containerWidth = geometry.width || this.containerHeight;
    const availableWidth = containerWidth * 0.95;
    this.barDepth = barDepth;

    let filteredStocksForUpdate = this.applySipFiltersToStocks(stocks);

    const stockMap = new Map(filteredStocksForUpdate.map(stock => [stock.ticker, stock]));

    if (!this.stockBars) {
        this.stockBars = new Map();
    }

    const allHeightValues = window.StockCubeCore.computeHeightValuesFromBars(this.stockBars, stockMap, heightValueFn);
    const currentFrameMax = Math.max(...allHeightValues.map(val => Math.abs(val)));
    let maxHeightValue = window.StockCubeCore.trackMaxHeightValue.call(
        this, currentFrameMax, heightValueFn
    );
    const hasNegativeValues = window.StockCubeCore.resolveNegativeValues.call(this, allHeightValues);

    this.zeroPlane = 0;
    const heightScale = window.StockCubeCore.computeHeightScale(targetHeight, maxHeightValue, hasNegativeValues);
    this.heightScale = heightScale;

    let { gridSize, maxRows, totalBars } = window.StockCubeCore.computeGridLayout(
        filteredStocksForUpdate.length
    );

    if (fixed) {
        gridSize = 10;
        maxRows = 5;
    }

    const hasInitialPositions = Array.from(this.stockBars.values()).every(bar =>
        typeof bar.x !== 'undefined' && typeof bar.z !== 'undefined'
    );

    let positionMap = new Map();

    if (fixed && hasInitialPositions) {
        positionMap = window.StockCubeCore.buildFixedGridPositionMap(this.stockBars, barWidth, spacing, gridSize);

        const existingTickers = new Set(this.stockBars.keys());
        const newStocks = filteredStocksForUpdate.filter(stock => !existingTickers.has(stock.ticker));
        if (newStocks.length > 0) {
            if (__LOG_WARN__) AppLog.warn(`Unexpected new stocks in fixed mode: ${newStocks.map(s => s.ticker).join(', ')}`);
        }
    } else {
        let allFilteredStocks = filteredStocksForUpdate
            .map(stock => {
                const heightValue = heightValueFn(stock);
                const height = this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
                return { ticker: stock.ticker, stock, heightValue, height };
            })
            .sort((a, b) => {
                const direction = this.sortDirection || 'asc';
                if (this.sortOrderFn) {
                    const valA = this.sortOrderFn(a.stock);
                    const valB = this.sortOrderFn(b.stock);
                    return direction === 'asc' ? valA - valB : valB - valA;
                }
                const valA = heightValueFn(a.stock);
                const valB = heightValueFn(b.stock);
                return direction === 'asc' ? valA - valB : valB - valA;
            });

        allFilteredStocks = allFilteredStocks.slice(0, totalBars);
        allFilteredStocks.forEach((item, index) => {
            const col = index % gridSize;
            const unit = barWidth + spacing;
            const x = unit * (col - gridSize / 2 + 0.5);
            const { z } = this.calculateZPositions(maxRows, totalBars, 'grid', index, 0, gridSize);
            positionMap.set(item.ticker, { x, z });
        });
    }

    if (!fixed) {
        window.StockCubeCore.removeOrphanedBars(this.stockBars, positionMap, this.el);
    }

    const isMmapChart = this.data?.dataSource === 'mmap';

    for (const [ticker, pos] of positionMap) {
        let barInfo = this.stockBars.get(ticker);
        const stock = stockMap.get(ticker);
        let heightValue, height, labelText, color;
        let labelX, primaryLabelY, barBaseY;

        if (stock) {
            heightValue = heightValueFn(stock);
            height = this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
            labelText = labelValueFn(stock);
            color = this.getGradientColor(heightValue);
        }

        const { absHeight, barBaseY: _barBaseY, primaryLabelY: _primaryLabelY } = window.StockCubeCore.computeBarYPositions(
            height, this.zeroPlane, this.labelYOffset, targetHeight
        );
        barBaseY = _barBaseY;
        primaryLabelY = _primaryLabelY;
        const unit = barWidth + spacing;
        const col = Math.round(pos.x / unit + gridSize / 2 - 0.5);
        const centerCol = Math.floor(gridSize / 2);

        if (!barInfo) {
            const { bar, primaryLabel } = this.createStockBar(
                stock,
                pos.x,
                pos.z,
                height,
                color,
                labelValueFn,
                container,
                this.zeroPlane,
                barWidth,
                barDepth,
                'true',
            );

            barInfo = {
                bar,
                primaryLabel,
                x: pos.x,
                z: pos.z,
                heightValue,
                y: barBaseY,
                stock,
                lastHeight: height,
                lastLabel: labelValueFn(stock)
            };

            bar.setAttribute('position', `${pos.x} ${barBaseY + (this.barYOffset || 0)} ${-pos.z}`);
            labelX = (gridSize % 2 !== 0 && col === centerCol) ? 0 : Math.max(-availableWidth / 2, Math.min(availableWidth / 2, pos.x));
            primaryLabel.setAttribute('value', labelText);
            primaryLabel.setAttribute('position', `${labelX} ${primaryLabelY} ${-pos.z}`);
            this.applyEmissiveHighlight(barInfo.bar, stock, color);
            this.stockBars.set(ticker, barInfo);
        } else {
            if (stock) {
                const updatedStock = { ...stock, tickerDetails: this.stockInfo[ticker]?.tickerDetails || stock.tickerDetails };
                barInfo.bar.__stockData = updatedStock;
                barInfo.bar.setAttribute('stock-bar', 'baseColor', color);
                if (this.colorMode === 'gradient') {
                    window.StockCubeCore.applyGradientColorAnimations(barInfo.bar, color);
                }
                if (barInfo.bar.__coloredBarNoTexture) {
                    barInfo.bar.__coloredBarNoTexture.setAttribute('color', color);
                    barInfo.bar.__coloredBarNoTexture.__currentColor = color;
                }
                barInfo.bar.setAttribute('color', color);
                barInfo.bar.setAttribute('material', {
                    color: color,
                    opacity: isMmapChart ? 1.0 : 0.65,
                    transparent: isMmapChart ? false : true,
                    side: 'double',
                    blending: 'normal',
                    depthTest: true,
                    depthWrite: true
                });
                this.applyEmissiveHighlight(barInfo.bar, updatedStock, color);
            } else {
                heightValue = barInfo.heightValue;
                height = this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
                labelText = barInfo.lastLabel;
                color = barInfo.bar.getAttribute('color');
            }

            barInfo.bar.removeAttribute('animation');
            barInfo.bar.removeAttribute('animation__position');
            barInfo.primaryLabel.removeAttribute('animation');

            let finalBarX, finalBarZ;

            if (!fixed) {
                const positionAnimName = `animation__pos_${ticker}`;
                barInfo.bar.setAttribute(positionAnimName, {
                    property: 'position',
                    to: `${pos.x} ${barBaseY} ${-pos.z}`,
                    dur: 400,
                    easing: 'easeInOutQuad'
                });
                labelX = (gridSize % 2 !== 0 && col === centerCol) ? 0 : Math.max(-availableWidth / 2, Math.min(availableWidth / 2, pos.x));
                barInfo.primaryLabel.setAttribute('value', labelText);

                let finalLabelY = primaryLabelY;
                if (this.rotationData) {
                    barInfo.lastHeight = height;
                    const rotationBasedPosition = this.calculateLabelPosition?.(barInfo, this.rotationData, labelX, -pos.z);
                    if (rotationBasedPosition) {
                        finalLabelY = rotationBasedPosition.y;
                    }
                }
                barInfo.primaryLabel.setAttribute('position', `${labelX} ${finalLabelY} ${-pos.z}`);
                barInfo.primaryLabel.setAttribute('animation', {
                    property: 'position',
                    to: `${labelX} ${finalLabelY} ${-pos.z}`,
                    dur: 400,
                    easing: 'easeInOutQuad'
                });
                finalBarX = pos.x;
                finalBarZ = pos.z;
            } else {
                const positionAnimName = `animation__pos_${ticker}`;
                barInfo.bar.setAttribute(positionAnimName, {
                    property: 'position',
                    to: `${pos.x} ${barBaseY} ${-pos.z}`,
                    dur: 400,
                    easing: 'easeInOutQuad'
                });
                const barPosRaw = barInfo.bar.getAttribute('position');
                const barPos = typeof barPosRaw === 'string' ?
                    { x: parseFloat(barPosRaw.split(' ')[0]), y: parseFloat(barPosRaw.split(' ')[1]), z: parseFloat(barPosRaw.split(' ')[2]) } :
                    barPosRaw || { x: pos.x, y: barBaseY, z: -pos.z };
                labelX = (gridSize % 2 !== 0 && col === centerCol) ? 0 : Math.max(-availableWidth / 2, Math.min(availableWidth / 2, barPos.x));
                barInfo.primaryLabel.setAttribute('value', labelText);

                let finalLabelY = primaryLabelY;
                if (this.rotationData) {
                    barInfo.lastHeight = height;
                    const rotationBasedPosition = this.calculateLabelPosition?.(barInfo, this.rotationData, labelX, -pos.z);
                    if (rotationBasedPosition) {
                        finalLabelY = rotationBasedPosition.y;
                    }
                }
                barInfo.primaryLabel.setAttribute('animation', {
                    property: 'position',
                    to: `${labelX} ${finalLabelY} ${-pos.z}`,
                    dur: 400,
                    easing: 'easeInOutQuad'
                });
                finalBarX = pos.x;
                finalBarZ = pos.z;
            }

            barInfo.bar.setAttribute('animation', {
                property: 'height',
                to: absHeight,
                dur: 400,
                easing: 'easeInOutQuad'
            });

            barInfo.x = finalBarX;
            barInfo.z = finalBarZ;
            barInfo.y = barBaseY;
            barInfo.heightValue = heightValue;
            barInfo.lastHeight = height;
            barInfo.lastLabel = labelText;
            this.stockBars.set(ticker, barInfo);
        }
    }

    setTimeout(() => {
        this.updateBillboards?.(true);
    }, 100);
};

window.StockCubeCore.createStockBar = function (
    stock, x, z, height, color, labelValueFn, container, zeroPlane,
    barWidth = 0.5, barDepth = 0.5, depthTest = "false",
    createLabel = true, enableDrop = false, dropDelayMs = 0, dropDurMs = 1200,
    labelFontSizeOverride = null, buildingTexturePath = null
) {
    const absHeight = Math.max(0.001, Math.abs(height));
    const isMmap = (this.data && this.data.dataSource === 'mmap') || this.isMmapMode;
    const isMmap2D = this.isMmapMode;

    const isPositive = isMmap2D && stock && typeof stock.todaysChangePerc === 'number'
        ? stock.todaysChangePerc >= 0
        : height >= 0;

    const barBaseY = isPositive ? zeroPlane + (absHeight / 2) : zeroPlane - (absHeight / 2);
    const primaryLabelY = isPositive
        ? zeroPlane + absHeight + this.labelYOffset
        : zeroPlane - absHeight - this.labelYOffset;

    if (!Number.isFinite(barBaseY)) {
        if (__LOG_WARN__) AppLog.warn('createStockBar: barBaseY is not finite', barBaseY);
        return { bar: document.createElement("a-box"), primaryLabel: null };
    }

    const bar = document.createElement("a-box");

    const globalAnimOn = !!this.getConfigSetting('isAnimationOn', true);
    const shouldDrop = enableDrop && this.isMmapMode === true && globalAnimOn;

    if (shouldDrop) {
        const dropOffset = 1.92;
        bar.setAttribute("position", `${x} ${barBaseY} ${-z - dropOffset}`);
    } else {
        bar.setAttribute("position", `${x} ${barBaseY + (this.barYOffset || 0)} ${-z}`);
    }

    bar.setAttribute("height", absHeight);
    bar.setAttribute("width", barWidth);
    bar.setAttribute("depth", barDepth);

    const targetOpacity = shouldDrop ? 0 : (isMmap2D ? 1.0 : 0.65);
    const barSide = "double";

    const materialOptions = {
        opacity: targetOpacity,
        transparent: true,
        side: barSide,
        blending: "normal",
        depthTest: depthTest,
    };

    if (isMmap2D) {
        materialOptions.renderOrder = 1;
    }

    bar.setAttribute("material", materialOptions);

    if (shouldDrop) {
        bar.setAttribute('class', ((bar.getAttribute('class') || '') + ' drop-in-progress').trim());
        bar.setAttribute('pointer-events', 'none');
    }

    bar.setAttribute('color', color);
    bar.setAttribute('stock-bar', 'baseColor', color);
    bar.__stockData = stock;

    this.applyEmissiveHighlight(bar, stock, color);

    if (shouldDrop) {
        bar.setAttribute('animation__drop', {
            property: 'position',
            to: `${x} ${barBaseY} ${-z}`,
            dur: dropDurMs,
            delay: Math.max(0, dropDelayMs),
            easing: 'easeOutQuart'
        });
        bar.setAttribute('animation__fade', {
            property: 'material.opacity',
            to: 0.65,
            dur: dropDurMs,
            delay: Math.max(0, dropDelayMs),
            easing: 'easeOutQuart'
        });
        bar.addEventListener('animationcomplete__drop', () => {
            bar.removeAttribute('pointer-events');
            const cls = (bar.getAttribute('class') || '').replace(/\bdrop-in-progress\b/, '').trim();
            if (cls) bar.setAttribute('class', cls); else bar.removeAttribute('class');
        }, { once: true });
    }

    let primaryLabel = null;

    if (createLabel) {
        primaryLabel = document.createElement("a-troika-text");
        primaryLabel.setAttribute("value", labelValueFn(stock));

        const platformStyle = (this.platformConfig && this.platformConfig.primaryLabelStyle) || {};
        const isThemeAware = platformStyle.themeAware || false;

        let labelColor, labelOutlineColor;
        if (isThemeAware) {
            const isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');
            labelColor = isLight ? "#111827" : "#FFFFFF";
            labelOutlineColor = isLight ? "#FFFFFF" : "#000000";
        } else {
            labelColor = platformStyle.color || 'white';
            labelOutlineColor = platformStyle.outlineColor || '#18181b';
        }

        let labelX = x;
        let labelY = primaryLabelY;
        let labelZ = -z;
        let labelRotation = '0 0 0';
        let useBillboard = true;
        let baseline = 'center';

        if (isMmap2D) {
            labelRotation = '-90 0 0';
            useBillboard = false;
        }

        primaryLabel.setAttribute('position', `${labelX} ${labelY} ${labelZ}`);
        primaryLabel.setAttribute('rotation', labelRotation);
        if (useBillboard) {
            primaryLabel.setAttribute('class', 'billboard');
        } else {
            primaryLabel.removeAttribute('class');
        }

        const isMmapForLabel = isMmap || this.isMmapMode;
        let fontSize = labelFontSizeOverride || (isMmapForLabel ? this.fontSize : (this.fontSize * 1.2));

        const canApplyMmapConfig = this.mmapPrimaryLabelConfig && isMmap2D;
        if (canApplyMmapConfig) {
            if (this.mmapPrimaryLabelConfig.fontSize !== undefined) {
                fontSize = fontSize * this.mmapPrimaryLabelConfig.fontSize;
                primaryLabel.setAttribute("font-size", fontSize);
            } else {
                primaryLabel.setAttribute("font-size", fontSize);
            }

            if (this.mmapPrimaryLabelConfig.scale !== undefined) {
                const scaleParts = this.mmapPrimaryLabelConfig.scale.split(' ');
                if (scaleParts.length >= 3) {
                    const scaleValue = parseFloat(scaleParts[0]);
                    if (!isNaN(scaleValue)) {
                        fontSize = fontSize * scaleValue;
                        primaryLabel.setAttribute("font-size", fontSize);
                    }
                }
                primaryLabel.setAttribute("scale", this.mmapPrimaryLabelConfig.scale);
            }

            const useOutline = this.mmapPrimaryLabelConfig.outlineWidth !== undefined &&
                this.mmapPrimaryLabelConfig.outlineWidth !== "0%";

            if (useOutline) {
                primaryLabel.setAttribute("outline-width", this.mmapPrimaryLabelConfig.outlineWidth);
                if (this.mmapPrimaryLabelConfig.outlineBlur !== undefined) {
                    primaryLabel.setAttribute("outline-blur", this.mmapPrimaryLabelConfig.outlineBlur);
                }
                if (this.mmapPrimaryLabelConfig.outlineColor !== undefined) {
                    primaryLabel.setAttribute("outline-color", this.mmapPrimaryLabelConfig.outlineColor);
                }
                primaryLabel.setAttribute("stroke-width", "0%");
            } else {
                primaryLabel.setAttribute("stroke-width", "2.5%");
                primaryLabel.setAttribute("stroke-color", labelOutlineColor);
                primaryLabel.setAttribute("stroke-opacity", "1.0");
                primaryLabel.setAttribute("outline-width", "0%");
            }
        } else {
            primaryLabel.setAttribute("font-size", fontSize);
            if (platformStyle.strokeWidth !== null && platformStyle.strokeWidth !== undefined) {
                primaryLabel.setAttribute("stroke-width", platformStyle.strokeWidth || "0");
            }
            if (platformStyle.outlineWidth !== null && platformStyle.outlineWidth !== undefined) {
                primaryLabel.setAttribute("outline-width", platformStyle.outlineWidth || "0%");
            }
            primaryLabel.setAttribute("outline-color", labelOutlineColor);
            if (platformStyle.outlineBlur !== null && platformStyle.outlineBlur !== undefined) {
                primaryLabel.setAttribute("outline-blur", platformStyle.outlineBlur);
            }
            if (platformStyle.outlineOpacity !== null && platformStyle.outlineOpacity !== undefined) {
                primaryLabel.setAttribute("outline-opacity", platformStyle.outlineOpacity);
            }
        }

        primaryLabel.setAttribute("align", "center");
        primaryLabel.setAttribute("anchor", "center");
        primaryLabel.setAttribute("baseline", baseline);
        primaryLabel.setAttribute("depth-offset", platformStyle.depthOffset || "1");
        primaryLabel.setAttribute("color", labelColor);
        if (platformStyle.fillOpacity !== null && platformStyle.fillOpacity !== undefined) {
            primaryLabel.setAttribute("fill-opacity", platformStyle.fillOpacity);
        }

        try {
            if (primaryLabel.object3D) {
                primaryLabel.object3D.renderOrder = 9999;
            } else {
                primaryLabel.addEventListener('loaded', () => {
                    try {
                        if (primaryLabel.object3D) primaryLabel.object3D.renderOrder = 9999;
                    } catch (e) {
                        try { if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] Deferred renderOrder set failed for primary label', e); } catch (_) { }
                    }
                }, { once: true });
            }
        } catch (e) {
            try { if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] Immediate renderOrder set failed for primary label', e); } catch (_) { }
        }

        if (this.platformConfig && this.platformConfig.platform === 'desktop') {
            const setLayer1 = (obj) => {
                if (obj) {
                    obj.layers.set(1);
                    obj.children.forEach(setLayer1);
                }
            };
            if (primaryLabel.object3D) {
                setLayer1(primaryLabel.object3D);
            } else {
                primaryLabel.addEventListener('loaded', () => {
                    if (primaryLabel.object3D) setLayer1(primaryLabel.object3D);
                }, { once: true });
            }
            primaryLabel.addEventListener('troika-text-ready', () => {
                if (primaryLabel.object3D) setLayer1(primaryLabel.object3D);
            });
        }

        if (useBillboard) {
            primaryLabel.setAttribute('class', 'billboard primary-label bloom-exclude');
        } else {
            primaryLabel.setAttribute('class', 'primary-label bloom-exclude');
        }

        setTimeout(() => {
            if (primaryLabel && primaryLabel.object3D) {
                primaryLabel.object3D.traverse((child) => {
                    if (child.material) {
                        const mats = Array.isArray(child.material) ? child.material : [child.material];
                        mats.forEach(mat => {
                            if (mat) {
                                if (mat.emissive) {
                                    mat.emissive.setHex(0x000000);
                                    mat.emissiveIntensity = 0;
                                }
                                mat.userData = mat.userData || {};
                                mat.userData.bloom = false;
                            }
                        });
                    }
                });
            }
        }, 100);

        if (shouldDrop && createLabel) {
            primaryLabel.setAttribute('material', { transparent: true, opacity: 0 });
            primaryLabel.setAttribute('visible', false);
            bar.addEventListener('animationcomplete__drop', () => {
                primaryLabel.setAttribute('visible', true);
                primaryLabel.setAttribute('material', { transparent: true, opacity: 0 });
                primaryLabel.setAttribute('animation__labelfadein', {
                    property: 'material.opacity',
                    to: 1,
                    dur: 250,
                    easing: 'easeOutQuad'
                });
                primaryLabel.addEventListener('animationcomplete__labelfadein', () => {
                    primaryLabel.setAttribute('material', { transparent: true, opacity: 1 });
                    primaryLabel.setAttribute('animation__labelblinkdown', {
                        property: 'material.opacity', from: 1, to: 0.4, dur: 720, easing: 'easeInOutSine'
                    });
                    primaryLabel.addEventListener('animationcomplete__labelblinkdown', () => {
                        primaryLabel.setAttribute('animation__labelblinkup', {
                            property: 'material.opacity', from: 0.4, to: 1, dur: 720, easing: 'easeInOutSine'
                        });
                        primaryLabel.addEventListener('animationcomplete__labelblinkup', () => {
                            primaryLabel.setAttribute('animation__labelblinkdown2', {
                                property: 'material.opacity', from: 1, to: 0.4, dur: 720, easing: 'easeInOutSine'
                            });
                            primaryLabel.addEventListener('animationcomplete__labelblinkdown2', () => {
                                primaryLabel.setAttribute('animation__labelblinkup2', {
                                    property: 'material.opacity', from: 0.4, to: 1, dur: 720, easing: 'easeInOutSine'
                                });
                            }, { once: true });
                        }, { once: true });
                    }, { once: true });
                }, { once: true });
            }, { once: true });
        }
    }

    window.StockCubeCore.registerBarHoverEffect(bar, primaryLabel, labelFontSizeOverride || this.fontSize, !shouldDrop);

    container.appendChild(bar);
    if (createLabel && primaryLabel) {
        container.appendChild(primaryLabel);
    }

    return { bar, primaryLabel };
};
